import { useEffect, useRef, useState } from "react";
import GuideTour from "./GuideTour";
import { useAuth } from "../context/AuthContext";
import { GUIDES, addButton, modalEl } from "../guide/guides";

// per user, so a different account on the same browser still gets its own tour
const seenKey = (uid, page) => `app.guide.done.${uid ?? "anon"}.${page}`;

const wasSeen = (uid, page) => {
  try { return !!localStorage.getItem(seenKey(uid, page)); } catch { return false; }
};
const markSeen = (uid, page) => {
  try { localStorage.setItem(seenKey(uid, page), "1"); } catch {}
};

const find = (target) => {
  const list = Array.isArray(target) ? target : [target];
  return list.some(t => (typeof t === "function" ? t() : document.querySelector(t)));
};

// click Cancel (second-to-last button) on the open modal
const closeModal = () => {
  const b = modalEl()?.querySelectorAll("button");
  if (b && b.length >= 2) b[b.length - 2].click();
};

// Runs the guided tour for whichever page is showing. `open` is owned by the
// app shell so the top-bar ? button can start it.
export default function GuideHost({ pageKey, open, setOpen }) {
  const [steps, setSteps] = useState([]);
  const openedForm = useRef(false);
  const guide = GUIDES[pageKey];
  const { user } = useAuth();
  const uid = user?.id;

  // build the step list once the page has rendered; drop steps that can't be shown
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      const hasAdd = !!addButton();
      const usable = (guide || []).filter(s => {
        if (s.form) return !!(s.open ? s.open() : hasAdd);
        if (s.optional) return find(s.target);
        return true;
      });
      if (usable.length === 0) setOpen(false);
      else {
        setSteps(usable);
        // shown once is enough, even if they leave the page mid-tour
        markSeen(uid, pageKey);
      }
    }, 60);
    return () => clearTimeout(id);
  }, [open, pageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // first visit to each page
  useEffect(() => {
    if (!guide || !uid || wasSeen(uid, pageKey)) return;
    const id = setTimeout(() => {
      if (!modalEl()) setOpen(true);
    }, 900);
    return () => clearTimeout(id);
  }, [pageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // navigating away ends the tour
  useEffect(() => {
    setOpen(false);
    setSteps([]);
  }, [pageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStep = (_, step) => {
    if (step.form) {
      if (!modalEl()) {
        (step.open ? step.open() : addButton())?.click();
        openedForm.current = true;
      }
    } else if (openedForm.current && modalEl()) {
      closeModal();
      openedForm.current = false;
    }
  };

  const handleClose = () => {
    if (openedForm.current && modalEl()) closeModal();
    openedForm.current = false;
    markSeen(uid, pageKey);
    setOpen(false);
    setSteps([]);
  };

  return (
    <GuideTour steps={steps} open={open && steps.length > 0}
               onClose={handleClose} onStep={handleStep} />
  );
}
